import { Component, OnInit } from '@angular/core';
import { StateService } from '../../../core/services/state.service';
import { ModalService } from '../../../core/services/modal.service';
import { Base64UrlService } from '../../../services/base64url.service';
import { DataTaskService } from '../../../core/services/data-task.service';
import { GridOptions } from '../../../core/components/grid/grid.types';
import { Grid } from '../../../core/components/grid/grid.model';
import { Item } from '../../../core/components/listbox/listbox.component';
import * as iconv from 'iconv-lite';

@Component({
    selector: 'tt-accounting-bunt',
    templateUrl: './accounting-bunt.component.html',
    styleUrls: ['./accounting-bunt.component.css'],
    standalone: false,
})
export class AccountingBuntComponent implements OnInit {
    public webPageName: string;

    accountingBuntKeyno;

    public linkId: string = 'formAccountingBuntLink';

    public edit?: Edit;

    private itemsListInvoices: Item[] = [];

    public grid?: GridOptions;

    public saving = false;

    public creatingInvoice = false;

    public updatingInvoice = false;

    public creatingFile = false;

    constructor(
        private stateService: StateService,
        private modal: ModalService,
        private base64: Base64UrlService,
        private datatask: DataTaskService,
    ) {
        this.webPageName = this.stateService.getCurrentName();
        this.accountingBuntKeyno = this.stateService.getStateParams()['accountingbunt_keyno'];
    }

    private async loadEdit() {
        if (!!this.accountingBuntKeyno) {
            this.edit = (await this.datatask.Post(1929, { accountingbunt_keyno: this.accountingBuntKeyno }))[0];
        }
    }

    private async loadInvoices() {
        if (!this.edit) return;

        if (!!this.edit.accountingbunt_keyno) {
            return (this.itemsListInvoices = await this.datatask.Post(1948, { accountingbunt_keyno: this.edit.accountingbunt_keyno }));
        }
    }

    private loadGrid() {
        if (!this.edit) return;

        if (!!this.edit.view_id && !!this.edit.p2_datatask_keyno) {
            if (!this.grid) {
                this.grid = new Grid()
                    .setRememberId(this.edit.view_id)
                    .setLoadDataFunction({
                        method: this.edit.p2_datatask_keyno as number,
                        parameters: () => this.edit,
                    })
                    .setKendoConfig({ height: '100%' })
                    .setToolbar({ wrapping: true });
            } else {
                this.grid?.gridfunc?.read();
            }
        }
    }

    public async saveChanges() {
        if (!!this.edit) {
            try {
                this.saving = true;

                const response = (await this.datatask.Post(1930, this.edit))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({
                        type: 'warning',
                        title: 'error',
                        translateTitle: true,
                        message: response.errormessage,
                    });
                } else {
                    this.stateService.back();
                }
            } finally {
                this.saving = false;
            }
        }
    }

    private async progressInvoice() {
        if (this.itemsListInvoices.length === 0 || !this.edit) return;

        let count = 0;

        const progressModal = this.modal.openProgressDialog({
            rememberId: 'w_accountingbunt_progressmodal',
            value: count,
            max: this.itemsListInvoices.length,
        });

        for (let row of this.itemsListInvoices) {
            const { errorcode, errormessage } = (await this.datatask.Post(1950, { item_id: row['item_id'] }))[0];

            if ('' + errorcode !== '0') {
                progressModal.updateStatus('invalid');
                progressModal.updateInvalidText(errormessage);
                break;
            }

            count = count + 1;
            progressModal.updateValue(count);
        }

        if (count === this.itemsListInvoices.length) {
            progressModal.updateStatus('finish');
        }

        this.creatingInvoice = false;
        this.updatingInvoice = false;

        await this.loadEdit();
        this.loadGrid();
    }

    public async createFile() {
        if (!!this.edit) {
            try {
                this.creatingFile = true;

                const response = (await this.datatask.Post(1953, { accountingbunt_keyno: this.edit.accountingbunt_keyno }))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({
                        type: 'warning',
                        title: 'error',
                        translateTitle: true,
                        message: response.errormessage,
                    });
                } else {
                    const elem = document.getElementById(this.linkId);
                    const text = this.base64.urlDecode(response?.filecontent);

                    if (!elem || !text) return;

                    const encodeWithAnsi = this.edit.setting_encode_with_ansi === '1';
                    const fileContent = encodeWithAnsi ? new Uint8Array(iconv.encode(text, 'windows-1252')) : text;
                    const charset = encodeWithAnsi ? 'windows-1252' : 'utf-8';
                    const xmlData = new Blob([fileContent], { type: `text/xml;charset=${charset}` });
                    const xmlUrl = URL.createObjectURL(xmlData);

                    elem.setAttribute('href', xmlUrl);
                    elem.setAttribute('download', response?.filename ?? '');
                    elem.click();

                    URL.revokeObjectURL(xmlUrl);
                }
            } finally {
                this.creatingFile = false;
            }
        }
    }

    public async createInvoice() {
        this.creatingInvoice = true;
        this.progressInvoice();
    }

    public async updateInvoice() {
        this.updatingInvoice = true;
        this.progressInvoice();
    }

    private async init() {
        await this.loadEdit();
        this.loadInvoices();
        this.loadGrid();
    }

    ngOnInit() {
        this.init();
    }
}

interface Edit {
    accountingbunt_keyno: string;
    bunt_datetime: string;
    modified_datetime: string;
    sent_datetime: string;
    bunttype: string;
    amount: string;
    sent_ok: string;
    registered_of: string;
    modified_of: string;
    number_of_invoices: string;
    company_name: string;
    bunt_date: string;
    stockplace_id: string;
    buntvalutacode: string;
    accountingbunt_errorcode: string;
    accountingbunt_errormessage: string;
    accountingbunt_errormessage_alert: string;
    nbr_of_accountingbuntdata: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    view_id: string;
    p2_datatask_keyno: string | number;
    setting_encode_with_ansi: '1' | '0';
    show_setting: '1' | '0';
}
